import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react'
import { cn } from '@/lib/utils'

export interface WheelOption {
  id: string
  label: string
  content: ReactNode
  disabled?: boolean
  // An empty choice ("nothing selected"): when it is the selection, the wheel shows no selection band.
  blank?: boolean
}

// One row of the wheel, in px: the list is translated by whole rows so the selected option lands in the middle one.
const ROW = 56
// Scroll input is summed and turns the wheel one option per this many pixels, so a trackpad and a mouse both feel direct.
const WHEEL_STEP_PX = 40
// A press that moves less than this is a click on a row, not a drag.
const CLICK_SLOP_PX = 4
// The drum: how far a row turns away per row of distance from the middle, and how quickly it fades.
const TILT_DEG = 24
const FADE_PER_ROW = 0.42

/**
 * A small vertical picker wheel in the manner of iOS: rows sit on a drum, so the one in the middle faces the reader and
 * the others turn away and fade the further they are — no zoom. Dragging turns the drum with the pointer (the wheel keeps
 * the pointer even when it leaves) and snaps to the nearest option on release; scrolling, the arrow keys or a click on
 * a row also turn it, always skipping disabled options. It is a listbox, so it reads and works as one with a keyboard
 * or a screen reader.
 */
export function ProfileWheel(props: {
  label: string
  options: WheelOption[]
  selected: number
  onSelect: (index: number) => void
  // Off, the wheel is a read-only label: only the current selection, changing with the legend's entrance, no picking.
  interactive?: boolean
  className?: string
}) {
  const root = useRef<HTMLDivElement>(null)
  // The wheel listener is attached once, so it reads the latest props through a ref.
  const latest = useRef(props)
  latest.current = props
  const wheelSum = useRef(0)
  const drag = useRef<{
    pointer: number
    startY: number
    startIndex: number
    row: number | null
    moved: boolean
  } | null>(null)
  // How far the rows sit from their resting place while a drag is between two options.
  const [offset, setOffset] = useState(0)

  // The nearest enabled option to `target`, searching outward; the current one when nothing else is enabled.
  const nearestEnabled = (target: number) => {
    const { options, selected } = latest.current
    const clamped = Math.max(0, Math.min(options.length - 1, target))
    for (let distance = 0; distance < options.length; distance++) {
      for (const index of [clamped - distance, clamped + distance]) {
        if (index >= 0 && index < options.length && !options[index]?.disabled) return index
      }
    }
    return selected
  }

  const step = (direction: 1 | -1) => {
    const { options, selected, onSelect } = latest.current
    for (let index = selected + direction; index >= 0 && index < options.length; index += direction) {
      if (!options[index]?.disabled) {
        onSelect(index)
        return
      }
    }
  }

  // Not React's onWheel: that listener is passive, and the page must not scroll while the wheel turns.
  useEffect(() => {
    const node = root.current
    if (!node) return
    let reset = 0
    const onWheel = (event: globalThis.WheelEvent) => {
      event.preventDefault()
      wheelSum.current += event.deltaY
      while (Math.abs(wheelSum.current) >= WHEEL_STEP_PX) {
        const direction = wheelSum.current > 0 ? 1 : -1
        wheelSum.current -= direction * WHEEL_STEP_PX
        step(direction)
      }
      // A pause ends the gesture, so the leftover of one flick never adds to the next.
      window.clearTimeout(reset)
      reset = window.setTimeout(() => {
        wheelSum.current = 0
      }, 160)
    }
    node.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      window.clearTimeout(reset)
      node.removeEventListener('wheel', onWheel)
    }
    // `step` reads everything through `latest`, so the listener never needs replacing.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') step(1)
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') step(-1)
    else return
    event.preventDefault()
  }

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // Captured, so a drag that wanders outside the wheel keeps turning it until the button is released.
    event.currentTarget.setPointerCapture(event.pointerId)
    // The row under the press, from its height in the wheel: rows are tilted in 3D, where the browser's hit-testing can
    // land on the list instead of the row. The middle row spans ROW…2·ROW, so each ROW of distance is one option.
    const top = event.currentTarget.getBoundingClientRect().top
    const row = props.selected + Math.floor((event.clientY - top) / ROW) - 1
    drag.current = {
      pointer: event.pointerId,
      startY: event.clientY,
      startIndex: props.selected,
      row: row >= 0 && row < props.options.length ? row : null,
      moved: false,
    }
  }
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    if (!current || current.pointer !== event.pointerId) return
    const delta = event.clientY - current.startY
    if (!current.moved && Math.abs(delta) < CLICK_SLOP_PX) return
    current.moved = true
    // Moving up brings later options to the middle; the rows follow the pointer and the selection follows the rows.
    const target = nearestEnabled(current.startIndex - Math.round(delta / ROW))
    if (target !== latest.current.selected) latest.current.onSelect(target)
    setOffset(delta + (target - current.startIndex) * ROW)
  }
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    if (!current || current.pointer !== event.pointerId) return
    drag.current = null
    setOffset(0)
    if (current.moved || current.row === null) return
    const option = props.options[current.row]
    if (option && !option.disabled && current.row !== props.selected) props.onSelect(current.row)
  }
  const onPointerCancel = () => {
    drag.current = null
    setOffset(0)
  }

  const dragging = offset !== 0
  const selectedOption = props.options[props.selected]

  if (props.interactive === false) {
    return (
      <div className={cn('flex h-14 min-w-0 items-center px-3', props.className)}>
        {/* Re-keyed by option, so a change replays the report profile card's entrance (comparison-profiles.tsx). */}
        <span key={selectedOption?.id} className="animate-in fade-in slide-in-from-bottom-2 flex min-w-0 duration-300">
          {selectedOption?.content}
        </span>
      </div>
    )
  }

  return (
    <div
      ref={root}
      role="listbox"
      aria-label={props.label}
      aria-activedescendant={selectedOption?.id}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      className={cn(
        'focus-visible:ring-ring/50 relative h-42 touch-none overflow-clip rounded-xl outline-none select-none focus-visible:ring-3',
        dragging ? 'cursor-grabbing' : 'cursor-grab',
        // `overflow-clip`, not hidden: a hidden box can still be scrolled, and focusing or clicking a row would scroll it
        // into view, knocking every row (and the band) off the middle.
        props.className,
      )}
    >
      {/* The selection band, as iOS draws it; an empty choice has nothing to band. */}
      <span
        aria-hidden
        className={cn(
          'bg-muted/60 absolute inset-x-0 top-14 h-14 rounded-xl transition-opacity duration-200',
          selectedOption?.blank && 'opacity-0',
        )}
      />
      <div
        // No easing while a drag is live, so the drum sticks to the pointer; the snap on release eases in.
        className={cn(
          'absolute inset-x-0 top-0 perspective-[480px]',
          !dragging && 'transition-transform duration-300 ease-out',
        )}
        style={{ transform: `translateY(${ROW - props.selected * ROW + offset}px)` }}
      >
        {props.options.map((option, index) => {
          const current = index === props.selected
          // Rows from the middle, continuous while dragging: 0 faces the reader, ±1 is the next row up or down.
          const distance = index - props.selected + offset / ROW
          const style: CSSProperties = {
            transform: `rotateX(${(-distance * TILT_DEG).toFixed(2)}deg)`,
            opacity: Math.max(0, 1 - Math.abs(distance) * FADE_PER_ROW) * (option.disabled ? 0.35 : 1),
          }
          return (
            <div
              key={option.id}
              id={option.id}
              data-row={index}
              role="option"
              aria-label={option.label}
              aria-selected={current}
              aria-disabled={option.disabled}
              className={cn(
                'relative flex h-14 items-center px-3 [backface-visibility:hidden]',
                !dragging && 'transition-[transform,opacity] duration-300 ease-out',
              )}
              style={style}
            >
              {option.content}
            </div>
          )
        })}
      </div>
    </div>
  )
}
